import React from "react";
import {
  FullWidthCTABase,
  Image,
  Description,
  CTAStyled,
  FinePrint,
  BackgroundImage,
} from "./full-width-cta.styled";
import { FullWidthCtaSliceFragment } from "../../types/graphql";
import { PrismicRichText } from "@prismicio/react";
import { htmlSerializer } from "../../utils/htmlSerializer";
import SalesforceContactForm from "../salesforce-contact-form/salesforce-contact-form";
import SalesforceFormContext, {
  useSalesforceViewContext,
} from "../../utils/salesforce-form-context";
import { track } from "../../utils/analytics";

enum Layout {
  xom_blue = "xom-blue",
}

interface IFullWidthCTAProps {
  slice: FullWidthCtaSliceFragment;
}

const FullWidthCTA = (props: IFullWidthCTAProps) => {
  const slice = props.slice;

  return slice ? (
    <SalesforceFormContext>
      <FullWidthCTAContext {...props} />
    </SalesforceFormContext>
  ) : (
    <></>
  );
};

const FullWidthCTAContext = (props: IFullWidthCTAProps) => {
  const primary = props.slice.primary;
  const setFormViewState = useSalesforceViewContext();

  return (
    <FullWidthCTABase
      className={`slice-section full-width-cta ${primary.layout}`}
    >
      <SalesforceContactForm
        leadSource={primary.salesforce_lead_source}
        disclaimer={primary.salesforce_form_disclaimer?.richText}
      >
        <div className="container">
          <div className="grid">
            {primary.layout !== Layout.xom_blue && (
              <div className="col-span-2">
                {primary.image && primary.image.url && (
                  <Image
                    src={primary.image.url}
                    alt={primary.image.alt || "Xometry Image"}
                  />
                )}
              </div>
            )}
            <div
              className={`col-span-4 ${
                primary.layout !== Layout.xom_blue
                  ? "md-col-span-6 lg-col-span-10"
                  : "lg-col-span-6"
              }`}
            >
              {primary.layout === Layout.xom_blue && primary.heading ? (
                <header>
                  <h2>{primary.heading}</h2>
                </header>
              ) : (
                <h3>{primary.heading}</h3>
              )}
              {primary.primary_description && (
                <Description>
                  <PrismicRichText
                    field={primary.primary_description.richText}
                    components={htmlSerializer}
                  />
                </Description>
              )}
              {primary.fine_print?.richText && (
                <FinePrint>
                  <PrismicRichText
                    field={primary.fine_print.richText}
                    components={htmlSerializer}
                  />
                </FinePrint>
              )}
              {primary.layout !== Layout.xom_blue && primary.cta_text && (
                <CTAStyled
                  kind="primary"
                  pageSection="full-width-cta"
                  ctaText={primary.cta_text}
                  action={primary.cta_action}
                  linkURL={primary.cta_link && primary.cta_link?.url}
                  target={primary.cta_link && primary.cta_link?.target}
                  driftBotInteractionID={primary.drift_bot_interaction_id}
                />
              )}
            </div>
            {primary.layout === Layout.xom_blue && (
              <div className="col-span-4 lg-col-span-6">
                {/* Open Salesforce form CTA */}
                {primary.cta_action === "Open Salesforce Form" &&
                primary.cta_text ? (
                  <CTAStyled
                    kind="outline-blue"
                    pageSection="full-width-cta"
                    ctaText={primary.cta_text}
                    onClickFunction={() => {
                      setFormViewState.setCurrentView("form");
                      track("Full Width CTA", {
                        text: "Open Salesforce Form Clicked",
                      });
                    }}
                  />
                ) : (
                  // Link CTA
                  primary.cta_text && (
                    <CTAStyled
                      kind="outline-blue"
                      pageSection="full-width-cta"
                      ctaText={primary.cta_text}
                      action={primary.cta_action}
                      linkURL={primary.cta_link && primary.cta_link?.url}
                      target={primary.cta_link && primary.cta_link?.target}
                      driftBotInteractionID={primary.drift_bot_interaction_id}
                    />
                  )
                )}
              </div>
            )}
          </div>
        </div>
        {primary.use_image_as_background && (
          <BackgroundImage>
            {primary.image && primary.image.url && (
              <Image
                src={primary.image.url}
                alt={primary.image.alt || "Xometry Image"}
              />
            )}
          </BackgroundImage>
        )}
      </SalesforceContactForm>
    </FullWidthCTABase>
  );
};

export default FullWidthCTA;
